/**
 * History Manager
 * Handles chat history CRUD operations
 *
 * @module HistoryManager
 * Sprint 32: OOA/OOD Refactoring
 */

import { ChatMessage } from '../types/ii-agent-types';
import { escapeHtml, safeFeatherReplace } from '../utils/html-utils';
import { ChatHistoryService, ChatHistoryEntry, ChatHistoryMessage } from '../../services/chat-history-service';

/**
 * Interface for history manager
 */
export interface IHistoryManager {
    load(): Promise<void>;
    search(query: string): Promise<void>;
    saveConversation(messages: ChatMessage[], chatId?: string, title?: string): Promise<ChatHistoryEntry>;
    loadEntry(chatId: string): Promise<ChatHistoryEntry | null>;
    deleteEntry(chatId: string): Promise<void>;
    renameEntry(chatId: string, newTitle: string): Promise<void>;
}

/**
 * Manager for chat history operations
 */
export class HistoryManager implements IHistoryManager {
    private chatHistoryService: ChatHistoryService;
    private currentPage: number = 0;
    private pageSize: number = 10;
    private totalCount: number = 0;
    private searchQuery: string = '';
    private isLoading: boolean = false;
    private containerId: string;
    private onEntrySelect?: (entry: ChatHistoryEntry) => void;

    constructor(containerId: string = 'history-list', onEntrySelect?: (entry: ChatHistoryEntry) => void) {
        this.chatHistoryService = ChatHistoryService.getInstance();
        this.containerId = containerId;
        this.onEntrySelect = onEntrySelect;
    }

    /**
     * Load chat history from canister
     */
    async load(): Promise<void> {
        if (this.isLoading) {
            console.log('Already loading chat history, skipping...');
            return;
        }

        this.isLoading = true;
        console.log('Loading chat history...');

        const historyList = document.getElementById(this.containerId);
        const historyLoading = document.getElementById('history-loading');
        const historyEmpty = document.getElementById('history-empty');
        const historyPagination = document.getElementById('history-pagination');

        if (!historyList) {
            console.warn('history-list element not found');
            this.isLoading = false;
            return;
        }

        // Show loading state
        if (historyLoading) historyLoading.classList.remove('hidden');
        if (historyEmpty) historyEmpty.classList.add('hidden');
        if (historyPagination) historyPagination.classList.add('hidden');

        // Clear existing items
        const existingItems = historyList.querySelectorAll('.history-item');
        existingItems.forEach(item => item.remove());

        try {
            let entries: ChatHistoryEntry[];
            let totalCount: number;

            if (this.searchQuery) {
                entries = await this.chatHistoryService.search(this.searchQuery, this.pageSize);
                totalCount = entries.length;
            } else {
                const offset = this.currentPage * this.pageSize;
                const response = await this.chatHistoryService.list(this.pageSize, offset);
                entries = response.entries;
                totalCount = response.total_count;
            }

            this.totalCount = totalCount;

            // Hide loading
            if (historyLoading) historyLoading.classList.add('hidden');

            if (entries.length === 0) {
                if (historyEmpty) historyEmpty.classList.remove('hidden');
            } else {
                this.renderList(entries, historyList);

                if (totalCount > this.pageSize && historyPagination) {
                    historyPagination.classList.remove('hidden');
                    this.updatePagination();
                }
            }

            console.log(`Loaded ${entries.length} history entries (total: ${totalCount})`);

        } catch (error) {
            console.error('Failed to load chat history:', error);

            if (historyLoading) historyLoading.classList.add('hidden');

            historyList.innerHTML = `
                <div class="text-center py-8 text-red-500">
                    <i data-feather="alert-circle" class="w-8 h-8 mx-auto mb-2"></i>
                    <p>Failed to load chat history</p>
                    <p class="text-sm text-gray-500 mt-1">${(error as Error).message}</p>
                    <button class="btn btn-secondary mt-4" id="btn-retry-history">
                        <i data-feather="refresh-cw"></i>
                        Retry
                    </button>
                </div>
            `;
            safeFeatherReplace();

            // Add retry handler
            const retryBtn = document.getElementById('btn-retry-history');
            if (retryBtn) {
                retryBtn.addEventListener('click', () => this.load());
            }
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Search chat history
     */
    async search(query: string): Promise<void> {
        console.log('Searching history for:', query);
        this.searchQuery = query.trim();
        this.currentPage = 0;
        await this.load();
    }

    /**
     * Clear search and reload
     */
    async clearSearch(): Promise<void> {
        this.searchQuery = '';
        this.currentPage = 0;
        await this.load();
    }

    /**
     * Save conversation to history
     */
    async saveConversation(messages: ChatMessage[], chatId?: string, title?: string): Promise<ChatHistoryEntry> {
        const historyMessages: ChatHistoryMessage[] = messages.map(m => ({
            id: this.chatHistoryService.generateMessageId(),
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.content,
            timestamp: m.timestamp * 1_000_000 // Convert to nanoseconds
        }));

        return this.chatHistoryService.saveConversation(historyMessages, chatId, title);
    }

    /**
     * Load a specific history entry
     */
    async loadEntry(chatId: string): Promise<ChatHistoryEntry | null> {
        console.log('Loading history entry:', chatId);
        return this.chatHistoryService.get(chatId);
    }

    /**
     * Delete a history entry
     */
    async deleteEntry(chatId: string): Promise<void> {
        console.log('Deleting history entry:', chatId);
        await this.chatHistoryService.delete(chatId);
        await this.load();
    }

    /**
     * Rename a history entry
     */
    async renameEntry(chatId: string, newTitle: string): Promise<void> {
        console.log('Renaming history entry:', chatId);
        await this.chatHistoryService.update({
            id: chatId,
            title: newTitle
        });
        await this.load();
    }

    /**
     * Generate AI summary for conversation title
     */
    async generateSummary(question: string, answer: string): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.fallbackSummary(question);
            }

            // Check if on mainnet
            const isMainnet = window.location.hostname.includes('icp0.io') ||
                             window.location.hostname.includes('ic0.app');

            if (isMainnet) {
                // Use frontend-assisted mode
                const apiKeyResult = await canisterClient.getApiKeyForFrontend();
                if (apiKeyResult && 'Ok' in apiKeyResult && apiKeyResult.Ok) {
                    const response = await fetch('https://api.anthropic.com/v1/messages', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKeyResult.Ok,
                            'anthropic-version': '2023-06-01',
                            'anthropic-dangerous-direct-browser-access': 'true'
                        },
                        body: JSON.stringify({
                            model: 'claude-3-5-haiku-20241022',
                            max_tokens: 50,
                            system: 'Generate very concise chat summaries. Always respond with exactly 3-5 words.',
                            messages: [{
                                role: 'user',
                                content: `Generate a 3-5 word summary:\n\nQuestion: ${question.substring(0, 500)}\nAnswer: ${answer.substring(0, 500)}`
                            }]
                        })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.content?.[0]?.text) {
                            let summary = data.content[0].text
                                .replace(/^["']|["']$/g, '')
                                .replace(/\.+$/, '')
                                .trim();
                            return summary.length > 50 ? summary.substring(0, 47) + '...' : summary;
                        }
                    }
                }
            } else {
                // Use canister mode
                const chatRequest = {
                    messages: [{
                        role: 'user',
                        content: `Generate a 3-5 word summary:\n\nQuestion: ${question.substring(0, 500)}\nAnswer: ${answer.substring(0, 500)}`
                    }],
                    context: ['Generate very concise chat summaries. Always respond with exactly 3-5 words.'],
                    conversation_id: []      // Candid opt text: [] for None
                };

                const result = await canisterClient.chatWithClaude(chatRequest);
                if ('Ok' in result && result.Ok.message) {
                    let summary = result.Ok.message
                        .replace(/^["']|["']$/g, '')
                        .replace(/\.+$/, '')
                        .trim();
                    return summary.length > 50 ? summary.substring(0, 47) + '...' : summary;
                }
            }
        } catch (error) {
            console.warn('Failed to generate summary:', error);
        }

        return this.fallbackSummary(question);
    }

    /**
     * Fallback summary (truncated question)
     */
    private fallbackSummary(question: string): string {
        return question.substring(0, 40) + (question.length > 40 ? '...' : '');
    }

    /**
     * Navigate to previous page
     */
    prevPage(): void {
        if (this.currentPage > 0) {
            this.currentPage--;
            this.load();
        }
    }

    /**
     * Navigate to next page
     */
    nextPage(): void {
        const maxPage = Math.ceil(this.totalCount / this.pageSize) - 1;
        if (this.currentPage < maxPage) {
            this.currentPage++;
            this.load();
        }
    }

    /**
     * Render the history list
     */
    private renderList(entries: ChatHistoryEntry[], container: HTMLElement): void {
        entries.forEach(entry => {
            const itemDiv = document.createElement('div');
            itemDiv.className = 'history-item p-4 bg-white border rounded-lg hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer';
            itemDiv.dataset.chatId = entry.id;

            const formattedDate = this.chatHistoryService.formatTimestamp(entry.updated_at);
            const messageCount = entry.messages?.length || 0;

            const firstQuestion = entry.messages?.find(m => m.role === 'user')?.content || '';
            const firstAnswer = entry.messages?.find(m => m.role === 'assistant')?.content || entry.preview || '';

            const truncatedQuestion = firstQuestion.length > 100
                ? firstQuestion.substring(0, 100) + '...'
                : firstQuestion;
            const truncatedAnswer = firstAnswer.length > 150
                ? firstAnswer.substring(0, 150) + '...'
                : firstAnswer;

            itemDiv.innerHTML = `
                <div class="flex items-start justify-between gap-3">
                    <div class="history-item-content flex-1 min-w-0">
                        <h4 class="font-semibold text-purple-700 mb-2">${escapeHtml(entry.title)}</h4>
                        <div class="mb-2">
                            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Question:</span>
                            <p class="text-sm text-gray-700 mt-0.5 line-clamp-2">${escapeHtml(truncatedQuestion)}</p>
                        </div>
                        <div class="mb-2">
                            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Ada:</span>
                            <p class="text-sm text-gray-600 mt-0.5 line-clamp-2 italic">${escapeHtml(truncatedAnswer)}</p>
                        </div>
                        <div class="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span><i data-feather="clock" class="w-3 h-3 inline"></i> ${formattedDate}</span>
                            <span><i data-feather="message-square" class="w-3 h-3 inline"></i> ${messageCount} messages</span>
                        </div>
                    </div>
                    <div class="flex items-center gap-1">
                        <button class="history-rename-btn p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Rename">
                            <i data-feather="edit-2" class="w-4 h-4"></i>
                        </button>
                        <button class="history-delete-btn p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                            <i data-feather="trash-2" class="w-4 h-4"></i>
                        </button>
                    </div>
                </div>
            `;

            // Add click handlers
            const contentDiv = itemDiv.querySelector('.history-item-content');
            if (contentDiv) {
                contentDiv.addEventListener('click', () => {
                    if (this.onEntrySelect) {
                        this.loadEntry(entry.id).then(fullEntry => {
                            if (fullEntry) {
                                this.onEntrySelect!(fullEntry);
                            }
                        });
                    }
                });
            }

            const renameBtn = itemDiv.querySelector('.history-rename-btn');
            if (renameBtn) {
                renameBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.promptRename(entry.id, entry.title);
                });
            }

            const deleteBtn = itemDiv.querySelector('.history-delete-btn');
            if (deleteBtn) {
                deleteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.confirmDelete(entry.id);
                });
            }

            container.appendChild(itemDiv);
        });

        safeFeatherReplace();
    }

    /**
     * Prompt user for new title
     */
    private async promptRename(chatId: string, currentTitle: string): Promise<void> {
        const newTitle = prompt('Enter a new title for this conversation:', currentTitle);
        if (newTitle === null) return;
        if (newTitle.trim() === '') {
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Title cannot be empty', 'warning');
            }
            return;
        }
        await this.renameEntry(chatId, newTitle.trim());
        if ((window as any).showToastNotification) {
            (window as any).showToastNotification('Conversation renamed', 'success');
        }
    }

    /**
     * Confirm deletion
     */
    private async confirmDelete(chatId: string): Promise<void> {
        if (!confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
            return;
        }
        await this.deleteEntry(chatId);
        if ((window as any).showToastNotification) {
            (window as any).showToastNotification('Conversation deleted', 'success');
        }
    }

    /**
     * Update pagination UI
     */
    private updatePagination(): void {
        const countEl = document.getElementById('history-count');
        const prevBtn = document.getElementById('btn-prev-page') as HTMLButtonElement;
        const nextBtn = document.getElementById('btn-next-page') as HTMLButtonElement;

        if (countEl) {
            const start = this.currentPage * this.pageSize + 1;
            const end = Math.min(start + this.pageSize - 1, this.totalCount);
            countEl.textContent = `Showing ${start}-${end} of ${this.totalCount}`;
        }

        if (prevBtn) {
            prevBtn.disabled = this.currentPage === 0;
        }

        if (nextBtn) {
            const maxPage = Math.ceil(this.totalCount / this.pageSize) - 1;
            nextBtn.disabled = this.currentPage >= maxPage;
        }
    }
}
